import React, { useState, useEffect } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MetricCard } from '@/components/Admin/MetricCard';
import { PrimaryMetricCard } from '@/components/Admin/PrimaryMetricCard';
import { SecondaryMetricCard } from '@/components/Admin/SecondaryMetricCard';
import { UserGrowthChart } from '@/components/Admin/UserGrowthChart';
import { RevenueChart } from '@/components/Admin/RevenueChart';
import { ConversionFunnel } from '@/components/Admin/ConversionFunnel';
import { PlanBreakdown } from '@/components/Admin/PlanBreakdown';
import { PaymentMethodChart } from '@/components/Admin/PaymentMethodChart';
import { WebhookHealth } from '@/components/Admin/WebhookHealth';
import { ChurnAnalysis } from '@/components/Admin/ChurnAnalysis';

interface DashboardMetrics {
  totalUsers: number;
  activeUsers: number;
  trialUsers: number;
  premiumUsers: number;
  monthlyRevenue: number;
  annualRevenue: number;
  churnRate: number;
  conversionRate: number;
  userGrowth: any[];
  revenueData: any[];
  planBreakdown: any[];
  paymentMethods: any[];
  webhookStatus: any;
}

export function AdminDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ========== PROTEÇÃO: SÓ ADMIN ACESSA ==========
  const ADMIN_EMAILS = ['brunobgs1888@gmail.com'];
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">🔒 Acesso Negado</h1>
          <p className="text-slate-400">Você não tem permissão para acessar o dashboard admin.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar empresas diretamente
      const { data: empresasData, error: empresasError } = await supabase
        .from('empresas')
        .select('*')
        .eq('is_deleted', false);

      if (empresasError) throw empresasError;

      // Buscar payments/asaas_subscriptions diretamente
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('asaas_subscriptions')
        .select('*')
        .eq('is_deleted', false);

      if (paymentsError) throw paymentsError;

      // Processar métricas
      const empresas = empresasData || [];
      const payments = paymentsData || [];
      const processedMetrics = processMetrics(empresas, payments);
      setMetrics(processedMetrics);
    } catch (err) {
      console.error('Erro ao buscar métricas:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const processMetrics = (empresas: any[], payments: any[]) => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Contar usuários
    const totalUsers = empresas.length;
    const premiumUsers = empresas.filter(e => e.is_premium).length;
    const trialUsers = empresas.filter(e => !e.is_premium).length;
    const activeUsers = empresas.filter(e => {
      const trialEnd = new Date(e.trial_end_date);
      return trialEnd > now || e.is_premium;
    }).length;

    // Calcular receita
    const monthlyRevenue = payments
      .filter(p => {
        const date = new Date(p.created_at);
        return date > thirtyDaysAgo && p.status === 'active';
      })
      .reduce((sum, p) => sum + (p.value || 0), 0);

    const annualRevenue = payments
      .filter(p => p.status === 'active')
      .reduce((sum, p) => sum + (p.value || 0), 0);

    // Calcular churn (usuários que deixaram de ser premium nos últimos 30 dias)
    const churnedUsers = empresas.filter(e => {
      const updated = new Date(e.updated_at);
      return updated > thirtyDaysAgo && !e.is_premium && updated > thirtyDaysAgo;
    }).length;

    const churnRate = totalUsers > 0 ? (churnedUsers / totalUsers) * 100 : 0;

    // Conversion rate: trial → premium
    const conversionRate = trialUsers > 0 ? (premiumUsers / (premiumUsers + trialUsers)) * 100 : 0;

    // Crescimento de usuários (últimos 30 dias)
    const userGrowth = generateUserGrowthData(empresas);

    // Dados de receita
    const revenueData = generateRevenueData(payments);

    // Breakdown por plano
    const planBreakdown = generatePlanBreakdown(payments);

    // Métodos de pagamento
    const paymentMethods = generatePaymentMethodData(payments);

    // Status dos webhooks
    const webhookStatus = generateWebhookStatus();

    return {
      totalUsers,
      activeUsers,
      trialUsers,
      premiumUsers,
      monthlyRevenue,
      annualRevenue,
      churnRate,
      conversionRate,
      userGrowth,
      revenueData,
      planBreakdown,
      paymentMethods,
      webhookStatus,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <Loader2 className="w-12 h-12 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="w-full max-w-2xl bg-gradient-to-br from-amber-900/20 to-red-900/20 border-red-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-amber-200">⚠️ Configuração Necessária</CardTitle>
            <CardDescription className="text-amber-100/70">
              O dashboard precisa de permissões no Supabase
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-amber-50">{error}</p>
            <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-700/50">
              <p className="text-sm text-slate-300 mb-2">
                <strong>Solução:</strong> Execute este comando no Supabase SQL Editor:
              </p>
              <pre className="text-xs text-slate-400 overflow-x-auto whitespace-pre-wrap break-words">
                {`-- Adicionar RLS policy para admin dashboard
CREATE POLICY "Admin pode ler todas empresas"
  ON public.empresas
  FOR SELECT
  USING (auth.jwt() ->> 'email' = 'brunobgs1888@gmail.com');

CREATE POLICY "Admin pode ler todos payments"
  ON public.payments
  FOR SELECT
  USING (auth.jwt() ->> 'email' = 'brunobgs1888@gmail.com');`}
              </pre>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              ↻ Tentar novamente
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#08080b' }}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-1" style={{ color: '#f5f5f7' }}>Visão Geral</h1>
          <p style={{ color: '#8a8a95' }}>Métricas de negócio do Ateliê Pro</p>
        </div>

        {/* KPI Cards */}
        {metrics && (
          <>
            {/* Row 1: 3 Main KPIs with Sparklines */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <PrimaryMetricCard
                title="Usuários Ativos"
                value={metrics.activeUsers}
                trend="+8%"
                data={metrics.userGrowth}
              />
              <PrimaryMetricCard
                title="MRR — Receita Mensal"
                value={`R$ ${metrics.monthlyRevenue.toFixed(2)}`}
                trend="+22%"
                data={metrics.revenueData}
              />
              <PrimaryMetricCard
                title="Taxa de Conversão"
                value={`${metrics.conversionRate.toFixed(1)}%`}
                trend="Funll"
                data={metrics.userGrowth}
              />
            </div>

            {/* Row 2: Secondary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <SecondaryMetricCard
                title="Total de Usuários"
                value={metrics.totalUsers}
                trend="+12%"
              />
              <SecondaryMetricCard
                title="Premium"
                value={metrics.premiumUsers}
                trend="+15%"
              />
              <SecondaryMetricCard
                title="Trial"
                value={metrics.trialUsers}
                trend="-3%"
              />
              <SecondaryMetricCard
                title="Churn Rate"
                value={`${metrics.churnRate.toFixed(1)}%`}
                trend="30d"
                isNegative={metrics.churnRate > 5}
              />
            </div>

            {/* Row 3: Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <UserGrowthChart data={metrics.userGrowth} />
              <RevenueChart data={metrics.revenueData} />
            </div>

            {/* Row 4: More Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <ConversionFunnel
                totalUsers={metrics.totalUsers}
                trialUsers={metrics.trialUsers}
                premiumUsers={metrics.premiumUsers}
              />
              <PlanBreakdown data={metrics.planBreakdown} />
            </div>

            {/* Row 5: Additional Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <PaymentMethodChart data={metrics.paymentMethods} />
              <WebhookHealth status={metrics.webhookStatus} />
            </div>

            {/* Row 6: Churn Analysis */}
            <div className="mb-6">
              <ChurnAnalysis data={metrics.userGrowth} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ========== FUNÇÕES AUXILIARES ==========

function generateUserGrowthData(empresas: any[]) {
  // Gerar dados dos últimos 30 dias
  const data = [];
  for (let i = 29; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);

    const usersUpToDate = empresas.filter(e => {
      const created = new Date(e.created_at);
      return created <= date;
    }).length;

    data.push({
      date: date.toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' }),
      usuarios: usersUpToDate,
      premium: empresas.filter(e => {
        const created = new Date(e.created_at);
        return created <= date && e.is_premium;
      }).length,
    });
  }
  return data;
}

function generateRevenueData(payments: any[]) {
  const data: any = {};

  payments.forEach(p => {
    const date = new Date(p.created_at);
    const key = date.toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' });

    if (!data[key]) {
      data[key] = 0;
    }

    if (p.status === 'active') {
      data[key] += p.value || 0;
    }
  });

  return Object.entries(data)
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .slice(-30)
    .map(([date, value]) => ({
      date,
      receita: parseFloat((value as number).toFixed(2)),
    }));
}

function generatePlanBreakdown(payments: any[]) {
  const plans: any = {};

  payments.forEach(p => {
    const billingType = p.billing_type || 'Desconhecido';
    plans[billingType] = (plans[billingType] || 0) + (p.value || 0);
  });

  return Object.entries(plans).map(([name, value]) => ({
    name,
    value: parseFloat((value as number).toFixed(2)),
  }));
}

function generatePaymentMethodData(payments: any[]) {
  const methods: any = {};

  payments.forEach(p => {
    const method = p.billing_type || 'Outro';
    methods[method] = (methods[method] || 0) + 1;
  });

  return Object.entries(methods).map(([name, count]) => ({
    name,
    value: count as number,
  }));
}

function generateWebhookStatus() {
  // Simulado - em produção, buscar do banco de dados
  return {
    total: 2341,
    success: 2341,
    failed: 12,
    pending: 3,
  };
}
